import { inject, Signal } from '@angular/core';

import {
  injectMutation,
  injectQuery,
} from '@tanstack/angular-query-experimental';

import { firstValueFrom } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { ToastService } from '@services/toast.service';
import { DataObject, ObjectDefinition } from 'src/app/core/models/core.models';
import { sortPrincipalsByRole } from '../normalize/permissions.normalize';
import { CompanyApiService } from '../services/company-api.service';
import {
  Permission,
  PermissionsResponse,
} from 'src/app/core/models/object.models';

/**
 * Query config shared by both permission reads. Background refetches are off because
 * the permissions matrix **edits the fetched arrays** (add/remove a principal, change
 * roles) before posting them, so one landing mid-edit would reseed the matrix and
 * drop pending changes. Reopening the dialog still refetches.
 */
const PERMISSIONS_QUERY_OPTIONS = {
  staleTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: false,
} as const;

function toPermissionsResult(
  response: PermissionsResponse,
): PermissionsResponse {
  return {
    permissions: response.permissions,
    principals: sortPrincipalsByRole(response.principals ?? []),
  };
}

/**
 * Permissions for a concrete set of objects (POST /company/permissions). The
 * `DataObject` → `ObjectDefinition` mapping happens inside the hook, so the query key
 * carries only the identity triples.
 */
export function injectObjectsPermissionsQuery(
  objects: Signal<DataObject[] | null>,
) {
  const companyApi = inject(CompanyApiService);

  const objectDefinitions = (): ObjectDefinition[] =>
    (objects() ?? []).map((object) => ({
      object_id: object.id,
      object_class_id: object.object_class_id,
      object_type_id: object.object_type_id,
    }));

  return injectQuery(() => ({
    queryKey: ['company', 'permissions', objectDefinitions()],
    queryFn: () =>
      firstValueFrom(
        companyApi.getObjectsPermissions(objectDefinitions()),
      ).then(toPermissionsResult),
    enabled: !!objects()?.length,
    ...PERMISSIONS_QUERY_OPTIONS,
  }));
}

/**
 * Default permissions for an object class (POST /company/default_permissions) - what
 * a not-yet-created object starts from.
 */
export function injectDefaultPermissionsQuery(
  objectClassId: Signal<number | null>,
) {
  const companyApi = inject(CompanyApiService);

  return injectQuery(() => ({
    queryKey: ['company', 'default_permissions', objectClassId()],
    queryFn: () =>
      firstValueFrom(
        companyApi.getDefaultObjectClassPermissions(objectClassId()!),
      ).then(toPermissionsResult),
    enabled: objectClassId() !== null,
    ...PERMISSIONS_QUERY_OPTIONS,
  }));
}

/**
 * Save the permissions matrix (POST /company/update_objects_permissions).
 *
 * No invalidation: the host dialog closes on save and each open refetches, so there
 * is no stale entry to reconcile - and a `setQueryData` patch would have to guess how
 * the server reconciled the matrix.
 */
export function injectSaveObjectPermissionsMutation() {
  const companyApi = inject(CompanyApiService);
  const toast = inject(ToastService);
  const translate = inject(TranslateService);

  return injectMutation(() => ({
    mutationFn: ({
      objects,
      permissions,
    }: {
      objects: DataObject[];
      permissions: Permission[];
    }) =>
      firstValueFrom(companyApi.updateObjectsPermissions(objects, permissions)),
    onSuccess: () =>
      toast.showSuccess(
        translate.instant(
          'object-permissions.msg.premissions-saved-successfully',
        ),
      ),
  }));
}
